import treeIcon from '../../assets/tree.png'
import React from 'react'
import IconButton from '@material-ui/core/IconButton'
import InputBase from '@material-ui/core/InputBase'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import MapIcon from '@material-ui/icons/Map'
import RoomIcon from '@material-ui/icons/Room'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'
import { useTranslation } from 'react-i18next'
import SearchIcon from '@material-ui/icons/Search'
import WarningIcon from '@material-ui/icons/Warning'
import GroupIcon from '@material-ui/icons/Group'
import CircularProgress from '@material-ui/core/CircularProgress'
import { makeStyles } from '@material-ui/core/styles'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'
import CenterFocusWeakIcon from '@material-ui/icons/CenterFocusWeak'
import CloseIcon from '@material-ui/icons/Close'
import { useDebounce } from 'usehooks-ts'
import { useAutocompleteGeocoder } from '@graniot/common/adapters/geocoder'
import { useLocation } from '@graniot/common/context/LocationContext'
import { isValidCoordinate, getCoordinatesFromString } from '@graniot/common/utils/map'

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
}))

// Memoized style objects
const optionBoxStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
}

const contentStyles = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  flexGrow: 1,
  marginRight: 16
}

const textStyles = {
  minWidth: 0,
  flexGrow: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const iconStyles = {
  flexShrink: 0,
  marginRight: 8
}

const warningIconStyles = {
  color: 'orange'
}

const rightIconStyles = {
  flexShrink: 0,
  marginLeft: 8,
}

const iconsContainerStyles = {
  display: 'flex',
  flexShrink: 0
}

// Helper to get icon size
const getIconSize = (width) => (!isWidthUp('sm', width) ? 'small' : 'default')

// Helper to size image icons similarly to MUI icon sizes
const getIconPx = (width) => (!isWidthUp('sm', width) ? 20 : 24)

// Component for warning icons
const WarningIcons = React.memo(({ computed, width, t }) => (
  <div style={iconsContainerStyles}>
    {computed?.has_overlap && (
      <Tooltip title={t("This parcel overlaps with other parcels")}>
        <WarningIcon
          fontSize={getIconSize(width)}
          style={{ ...rightIconStyles, ...warningIconStyles }}
        />
      </Tooltip>
    )}
    {computed?.overlaps_protected_areas && (
      <Tooltip title={t("This parcel overlaps with protected area")}>
        <img
          src={treeIcon}
          alt="Protected area"
          style={{ ...rightIconStyles, width: getIconPx(width), height: getIconPx(width) }}
        />
      </Tooltip>
    )}
    {computed?.overlaps_indigenous_areas && (
      <Tooltip title={t("This parcel overlaps with indigenous area")}>
        <GroupIcon
          fontSize={getIconSize(width)}
          style={{ ...rightIconStyles, ...warningIconStyles }}
        />
      </Tooltip>
    )}
  </div>
))

function PublicParcelSelector({
  parcels,
  focusParcel,
  setFocusParcel,
  centerMap,
  width,
}) {
  const classes = useStyles()
  const { t } = useTranslation()
  const [inputValue, setInputValue] = React.useState('')
  const debouncedInputValue = useDebounce(inputValue, 300)
  const { setLocation } = useLocation()

  // Geocoding search
  const {
    data: geocodingData,
    isLoading: isGeocodingLoading,
  } = useAutocompleteGeocoder(
    debouncedInputValue,
    !debouncedInputValue || isValidCoordinate(debouncedInputValue)
  )

  // Sort parcels by priority (overlaps first) then by name
  const sortedParcels = React.useMemo(() => {
    if (!parcels) return []

    return [...parcels].sort((a, b) => {
      const computedA = a.properties?.computed_attributes || {}
      const computedB = b.properties?.computed_attributes || {}

      // Calculate priority for sorting (higher priority = shown first)
      const getPriority = (computed) => {
        if (computed.has_overlap && (computed.overlaps_protected_areas || computed.overlaps_indigenous_areas)) return 3
        if (computed.overlaps_protected_areas || computed.overlaps_indigenous_areas) return 2
        if (computed.has_overlap) return 1
        return 0
      }

      const priorityA = getPriority(computedA)
      const priorityB = getPriority(computedB)

      // Sort by priority first (descending), then by name
      if (priorityA !== priorityB) {
        return priorityB - priorityA
      }

      const nameA = String(a.properties?.name || '').toLowerCase()
      const nameB = String(b.properties?.name || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
  }, [parcels])

  // Combine parcels with geocoding results
  const combinedOptions = React.useMemo(() => {
    const options = []

    // Check if input is coordinates
    if (inputValue && isValidCoordinate(inputValue)) {
      const coords = getCoordinatesFromString(inputValue)
      if (coords) {
        options.push({
          place_name: inputValue,
          center: [coords.lng, coords.lat],
          bbox: null,
          isCoordinate: true
        })
      }
    }

    // Add filtered parcels
    if (inputValue) {
      const searchTerm = inputValue.toLowerCase()
      const filtered = sortedParcels.filter(parcel => {
        const name = String(parcel.properties?.name || '').toLowerCase()
        return name.includes(searchTerm)
      })
      options.push(...filtered)
    } else {
      options.push(...sortedParcels)
    }

    // Add geocoding results
    if (geocodingData?.features) {
      options.push(...geocodingData.features)
    }

    return options
  }, [sortedParcels, inputValue, geocodingData])

  // Reset input when parcels change
  React.useEffect(() => {
    setInputValue('')
    setFocusParcel(null)
  }, [parcels, setFocusParcel])

  const handleSelectionChange = React.useCallback((event, newValue) => {
    if (newValue) {
      // Check if it's a parcel or a location
      const isParcel = newValue?.properties?.name

      if (isParcel) {
        setFocusParcel(newValue)
        // Center map immediately on selection
        if (newValue.bbox) {
          centerMap(newValue.bbox)
        }
      } else {
        // It's a geocoding result
        setFocusParcel(null)
        if (newValue.bbox) {
          setLocation({ bbox: newValue.bbox })
          centerMap(newValue.bbox)
        } else if (newValue.center) {
          const latLng = [newValue.center[1], newValue.center[0]]
          setLocation({ latLng })
        }
      }
    }
  }, [setFocusParcel, centerMap, setLocation])

  const handleInputChange = React.useCallback((event, newInputValue) => {
    setInputValue(newInputValue)
  }, [])

  const handleCenterMap = React.useCallback(() => {
    if (focusParcel?.bbox) {
      centerMap(focusParcel.bbox)
    }
  }, [centerMap, focusParcel])

  const handleDeselectParcel = React.useCallback(() => {
    setFocusParcel(null)
    setInputValue('')
  }, [setFocusParcel])

  return (
    <Autocomplete
      id="public-parcel-selector"
      value={focusParcel}
      onChange={handleSelectionChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={combinedOptions}
      loading={isGeocodingLoading}
      style={{
        width: !isWidthUp('sm', width) ? '100%' : 350,
        marginBottom: 10,
      }}
      getOptionLabel={(option) => {
        if (option?.properties?.name) {
          return String(option.properties.name)
        } else if (option?.place_name) {
          return option.place_name
        }
        return ''
      }}
      getOptionSelected={(option, value) => {
        if (option?.id && value?.id) {
          return option.id === value.id
        }
        return false
      }}
      renderOption={(option) => {
        const isParcel = option?.properties?.name
        const computed = option?.properties?.computed_attributes || {}

        return (
          <Box component="li" sx={optionBoxStyles}>
            {isParcel ? (
              <>
                <div style={contentStyles}>
                  <MapIcon fontSize={getIconSize(width)} style={iconStyles} />
                  <Typography noWrap style={textStyles}>
                    {option.properties.name}
                  </Typography>
                </div>
                <WarningIcons computed={computed} width={width} t={t} />
              </>
            ) : (
              <div style={contentStyles}>
                <RoomIcon fontSize={getIconSize(width)} style={iconStyles} />
                <Typography noWrap style={textStyles}>
                  {option.place_name}
                </Typography>
              </div>
            )}
          </Box>
        )
      }}
      renderInput={(params) => (
        <Paper className={classes.root} ref={params.InputProps.ref}>
          <InputBase
            style={{
              backgroundColor: 'transparent',
              width: '100%',
            }}
            type="text"
            placeholder={t('Select parcel')}
            {...params.inputProps}
            startAdornment={
              <IconButton>
                <SearchIcon fontSize={!isWidthUp('sm', width) ? 'small' : 'default'} />
              </IconButton>
            }
            endAdornment={
              <>
                {isGeocodingLoading && (
                  <CircularProgress color="inherit" size={20} />
                )}
                {focusParcel && (
                  <>
                    <IconButton
                      onClick={handleCenterMap}
                      style={{ marginRight: -8 }}
                    >
                      <CenterFocusWeakIcon
                        fontSize={!isWidthUp('sm', width) ? 'small' : 'default'}
                      />
                    </IconButton>
                    <IconButton
                      onClick={handleDeselectParcel}
                      style={{ marginLeft: -8 }}
                    >
                      <CloseIcon
                        fontSize={!isWidthUp('sm', width) ? 'small' : 'default'}
                      />
                    </IconButton>
                  </>
                )}
              </>
            }
          />
        </Paper>
      )}
    />
  )
}

export default withWidth()(PublicParcelSelector)